import { Navigate, Route, Routes, BrowserRouter, useLocation } from "react-router-dom";
import { tmAllowedPaths } from "@/lib/tmPages";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import AcceptInvite from "@/pages/AcceptInvite";
import AppLayout from "@/pages/AppLayout";
import Dashboard from "@/pages/Dashboard";
import Drivers from "@/pages/Drivers";
import Reports from "@/pages/Reports";
import Chat from "@/pages/Chat";
import Customers from "@/pages/Customers";
import QuickLinks from "@/pages/QuickLinks";
import Audits from "@/pages/Audits";
import Renew from "@/pages/Renew";
import Sign from "@/pages/Sign";
import { Privacy, Terms, SmsTerms } from "@/pages/Legal";
import TextAlerts from "@/pages/TextAlerts";
import Randoms from "@/pages/Randoms";
import Signatures from "@/pages/Signatures";
import Applicants from "@/pages/Applicants";
import Apply from "@/pages/Apply";
import EmployerInquiry from "@/pages/EmployerInquiry";
import WasntMe from "@/pages/WasntMe";
import ChFollowup from "@/pages/ChFollowup";
import Certificates from "@/pages/Certificates";
import Billing from "@/pages/Billing";
import Profile from "@/pages/Profile";
import Consultant from "@/pages/Consultant";
import Fleet from "@/pages/Fleet";
import Inspect from "@/pages/Inspect";
import Company from "@/pages/Company";
import CustomerProfile from "@/pages/CustomerProfile";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";

function Protected({ children, admin }) {
  const { user, checking } = useAuth();
  const { pathname } = useLocation();
  if (checking) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  if (user.role === "terminal_manager" && pathname !== "/" && !tmAllowedPaths(user).some((p) => pathname.startsWith(p))) return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/inspect/:token" element={<Inspect />} />
            <Route path="/renew/:token" element={<Renew />} />
            <Route path="/sign/:token" element={<Sign />} />
            <Route path="/apply/:slug" element={<Apply />} />
            <Route path="/employer-inquiry/:token" element={<EmployerInquiry />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/sms-terms" element={<SmsTerms />} />
            <Route path="/text-alerts" element={<TextAlerts />} />
            <Route path="/wasnt-me/:token" element={<WasntMe />} />
            <Route path="/ch-followup/:token" element={<ChFollowup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/"
              element={
                <Protected>
                  <AppLayout />
                </Protected>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="drivers" element={<Drivers />} />
              <Route path="reports" element={<Reports />} />
              <Route path="chat" element={<Chat />} />
              <Route path="certificates" element={<Certificates />} />
              <Route path="billing" element={<Billing />} />
              <Route path="profile" element={<Profile />} />
              <Route path="consultant" element={<Consultant />} />
              <Route path="audits" element={<Audits />} />
              <Route path="signatures" element={<Signatures />} />
              <Route path="applicants" element={<Applicants />} />
              <Route path="randoms" element={<Randoms />} />
              <Route path="fleet" element={<Fleet />} />
              <Route path="company" element={<Company />} />
              <Route
                path="customers"
                element={
                  <Protected admin>
                    <Customers />
                  </Protected>
                }
              />
              <Route path="links" element={<Protected admin><QuickLinks /></Protected>} />
              <Route
                path="customers/:id"
                element={
                  <Protected admin>
                    <CustomerProfile />
                  </Protected>
                }
              />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}
